<template>
	<div class="board">
		<section class="container-fluid">
			<div class="row">
				<div class="col-12 boardbar">
					<label>Date</label>
					<datepicker class="datepicker-input" ref="openPicker" v-model="date"/>
					<button class="calendar-button" @click="openDatePicker">
						<i class="ss-calendar ss-glyphish-filled"></i>
					</button>
					<button class="button-action" @click="copyOpen = true" v-on:copy="copyBoard">Copy</button>
					<button v-if="board" @click="togglePublishClicked" class="button-action">{{ board.published ? 'Unpublish' : 'Publish' }}</button>
					<button class="button-action" @click="print">Print</button>
					<i v-if="saving" class="ss-floppydisk ss-glyphish-filled animate-fade"></i>
					<div id="split-toggle">
						<label>Full</label>
						<toggle-button :sync="true" v-model="splitView" :css-colors="true"/>
						<label>Dual</label>
					</div>
				</div>
			</div>
			<div class="row board-container">
				<div class="col-12">
					<AdminGrid :board="board" v-if="board && splitView === false" v-on:change="updateBoard" v-on:assignTasks="openAssignTasks" :split="false" :key="version"/>
					<div class="row split-row" v-if="splitView">
						<div class="col-6 left">
							<AdminGrid :board="board" v-if="board" v-on:change="updateBoard" v-on:assignTasks="openAssignTasks" :split="true" :splitPosition="1" :key="version"/>
						</div>
						<div class="col-6 right">
							<AdminGrid :board="board" v-if="board" v-on:change="updateBoard" v-on:assignTasks="openAssignTasks" :split="true" :splitPosition="2" :key="version"/>
						</div>
					</div>
				</div>
			</div>
		</section>
		<section class="container-fluid">
			<div class="row">
				<div class="col-12">
					<AllocationTabs :board="board" :filter="filter" :columns="columns" v-if="board" v-on:change="updateBoard" :key="version" :minimised="allocationTabsMinimised" v-on:toggle="allocationTabsMinimised = !allocationTabsMinimised"/>
				</div>
			</div>
		</section>
		<PublishModal v-if="publishOpen" @close="publishOpen = false" v-on:publish="publishBoard"/>
		<Copy v-if="copyOpen" @close="copyOpen = false" v-on:copy="copyBoard"/>
		<AssignTasks v-if="selectedHorse" v-on:close="selectedHorse = null" :horse="selectedHorse" :tasks="selectHorseTasks" v-on:assignTasks="assignTasks"/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import { Container, Draggable } from 'vue-smooth-dnd'
import board from '../../lib/board'
import AdminGrid from '../Board/Grids/Admin.vue'
import Datepicker from 'vuejs-datepicker'
import moment from 'moment'
import AllocationTabs from '../Board/AllocationTabs.vue'
import PublishModal from '../Board/Publish.vue'
import Copy from '../Board/Copy.vue'
import AssignTasks from '../Board/AssignTasks.vue'

export default { 
	components: {
		Container,
		Draggable,
		AdminGrid,
		AllocationTabs,
		AssignTasks,
		Datepicker,
		AllocationTabs,
		PublishModal,
		Copy
	},
	data() {
		return {
			board: null,
			date: '',
			publishOpen: false,
			copyOpen: false,
			selectedHorse: null,
			selectHorseTasks: null,
			splitView: false,
			version: 1,
			saving: false,
			dragging: true,
			allocationTabsMinimised: false,
			filter: {
				people: {
					search: ''
				},
				horses: {
					search: '',
					year: 0
				},
				locations: {
					id: 0
				}
			}
		}
	},
	computed: {
		columns() {
			return (this.$store.getters['lots/list'].length + 1) * (this.splitView ? 2 : 1)
		}
	},
	watch: {
		date: function () {
			this.loadBoard(this.date)
		},
		splitView: function(splitView) {
			window.localStorage.setItem('split', splitView)
		}
	},
	methods: {
		loadBoard(date) {
			const formattedDate = moment(date).format('YYYY-MM-DD')

			board.get(formattedDate).then(b => {
				this.board = b
			})
		},
		updateBoard(changes) {
			this.saving = true;
			if(changes) {
				changes.forEach(change => {
					switch(change.type) {
						case 'ASSIGN_HORSE':
							if(this.board.rows[change.target.row] === undefined) {
								//row does not exist so create it
								this.board.rows[change.target.row] = { person: null, lots: {} }
							}
							this.board.rows[change.target.row].lots[change.target.lot] = parseInt(change.entity)

							const horseAllocatedPosition = this.board.allocated.horses.indexOf(parseInt(change.entity))

							if(horseAllocatedPosition === -1) {
								//horse came from unallocated
								this.board.allocated.horses.push(parseInt(change.entity))
							}
							break;
						case 'ASSIGN_PERSON':
							if(this.board.rows[change.target.row] === undefined) {
								//row does not exist so create it
								this.board.rows[change.target.row] = { person: null, lots: {} }
							}
							this.board.rows[change.target.row].person = parseInt(change.entity)

							const personAllocatedPosition = this.board.allocated.people.indexOf(change.entity)

							if(personAllocatedPosition === -1) {
								//person came from unallocated
								this.board.allocated.people.push(parseInt(change.entity))
							}
							break;
						case 'CLEAR_HORSE':
							this.board.rows[change.target.row].lots[change.target.lot] = null
							break;
						case 'CLEAR_PERSON':
							this.board.rows[change.target.row].person = null
							break;
						case 'UNASSIGN_HORSE':
							this.board.allocated.horses.splice(this.board.allocated.horses.indexOf(parseInt(change.entity)), 1)
							break;
						case 'UNASSIGN_PERSON':
							this.board.allocated.people.splice(this.board.allocated.people.indexOf(parseInt(change.entity)), 1)
							break;
					}
				})
			}
			//This refreshes boards
			this.version++

			board.set(this.board.id, this.board.date, this.board.rows, this.board.published, this.board.tasks).then(res => {
				this.saving = false;
				this.board.id = res.id
				this.board.totalRows = res.totalRows
			})
		},
		findHorseBoardPosition(horse) {
			//XXX this is too much effort, need to separate horses and tasks
			for(let row in this.board.rows) {
				for(let lot in this.board.rows[row].lots) {
					if(this.board.rows[row].lots[lot] && this.board.rows[row].lots[lot].id === horse.id) {
						return { row, lot }
					}
				}
			}
		},
		openAssignTasks(args) {
			this.selectedHorse = args.horse
			this.selectHorseTasks = args.tasks
		},
		assignTasks(tasks) {
			const boardPosition = this.findHorseBoardPosition(this.selectedHorse)
			//TODO refactor this into a change
			this.board.tasks[this.selectedHorse.id] = tasks
			this.selectedHorse = null
			this.selectHorseTasks = null
			this.updateBoard()
		},
		togglePublishClicked() {
			if(this.board.published) {
				this.board.published = false
				this.updateBoard()
			} else {
				this.publishOpen = true
			}
		},
		publishBoard() {
			this.board.published = true
			this.publishOpen = false
			this.updateBoard()
		},
		copyBoard(date) {
			const formattedDate = moment(date).format('YYYY-MM-DD')

			board.get(formattedDate).then(b => {
				const currentId = this.board.id

				this.board = b
				this.board.date = moment(this.date).format('YYYY-MM-DD')
				this.board.id = currentId
				this.board.published = false
				this.updateBoard()
			})
			this.copyOpen = false
		},
		openDatePicker() {
			this.$refs.openPicker.showCalendar();
		},
		print() {
			window.print();
		}
	},
	mounted() {
		this.splitView = window.localStorage.getItem('split') == 'true'

		this.date = moment().add(1, 'd').format('D MMM YYYY')

		this.$store.commit('app/setLoading', true)

		const people = this.$store.dispatch('people/getList')
		const horses = this.$store.dispatch('horses/getList')
		const age = this.$store.dispatch('age/getList')
		const tasks = this.$store.dispatch('tasks/getList')
		const lots = this.$store.dispatch('lots/getList')
		const locations = this.$store.dispatch('locations/getList')
		const account = this.$store.dispatch('account/get')

		Promise.all([people, horses, age, tasks, lots, locations])
		.then(() => {
			this.$store.commit('app/setLoading', false);
			//load tomorrows board
			this.loadBoard(this.date);
		})
		.catch(err => {
			this.$store.commit('app/setLoading', false);
			this.$store.dispatch('app/addAlert', {
				type: 'error',
				message: 'An Error occurred, please try later',
				timeout: 3000
			})
		})
	}
}
</script>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/Pages/Board.vue